import { gql, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Radio,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { sortBy } from 'lodash';
import React, { useState } from 'react';

import { channelTypeLogo } from 'src/app/graph/graphSettingsPage/reportingPage/helpers/helpers';
import { variantRouteConfig } from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { AddNotificationWizardChannelFragment } from '../graphql';

export interface SelectChannelAndVariantState {
  variant?: string;
  createNewChannel?: boolean;
  targetChannel?: GraphQLTypes.AddNotificationWizardChannelFragment;
  newChannelType?: 'SlackChannel' | 'WebhookChannel';
  notificationType?:
    | 'SchemaPublishSubscription'
    | 'ScheduledSummary'
    | 'BuildSubscription'
    | 'ProposalLifecycleSubscription';
}

interface Props {
  isOpen: boolean;
  state: Readonly<SelectChannelAndVariantState> | null;
  canViewWebhooks: Boolean;
  onCreateChannelChange: (
    newOrExistingChannel: NonNullable<
      SelectChannelAndVariantState['createNewChannel']
    >,
  ) => void;
  onExistingChannelChange: (
    existingChannel: GraphQLTypes.AddNotificationWizardChannelFragment,
  ) => void;
  onVariantChange: (variant: string) => void;
  onNewChannelTypeChange: (
    newChannelType: NonNullable<SelectChannelAndVariantState['newChannelType']>,
  ) => void;
  onComplete: () => void;
  onPrevious: () => void;
  onClose: () => void;
}

const selectVariantAndChannelModalQuery = gql`
  query SelectVariantAndChannelModalQuery($graphId: ID!) {
    service(id: $graphId) {
      id
      variants {
        id
        name
      }
      channels {
        id
        ...AddNotificationWizardChannelFragment
      }
    }
  }
  ${AddNotificationWizardChannelFragment}
`;

// This is mainly to filter out PagerDuty channels from being options when
// setting up non-performance alerts subs.  If / when we fully integrate
// the alerts flows into this wizard, things will have to change a bit.
// Alternatively we may get this from the backend soon:
// see https://github.com/mdg-private/monorepo/pull/5224
function isAllowedChannelType(
  notificationType: SelectChannelAndVariantState['notificationType'],
  channelType: GraphQLTypes.SelectVariantAndChannelModalQuery_service_channels['__typename'],
) {
  switch (notificationType) {
    case 'SchemaPublishSubscription':
      return ['SlackChannel', 'WebhookChannel'].includes(channelType);
    case 'ScheduledSummary':
      return ['SlackChannel'].includes(channelType);
    case 'BuildSubscription':
    case 'ProposalLifecycleSubscription':
      return ['WebhookChannel'].includes(channelType);
    case undefined:
      return false;
    default:
      return assertUnreachableOrReturnDefault(
        notificationType,
        'Unknown notification type',
      );
  }
}

export function getSubscriptionTypeDisplayCopy(
  notificationType: SelectChannelAndVariantState['notificationType'],
) {
  switch (notificationType) {
    case 'SchemaPublishSubscription':
      return 'Please specify which variant you would like to receive schema change notifications for.';
    case 'ScheduledSummary':
      return 'Please specify which variant you would like to receive daily report notifications for.';
    case 'BuildSubscription':
      return 'Please specify which variant you would like to receive build status notifications for.';
    case 'ProposalLifecycleSubscription':
      return 'Please specify where you would like to receive schema proposals notifications.';
    case undefined:
      throw new Error(`notification type was unexpectedly undefined`);
    default:
      return assertUnreachableOrReturnDefault(
        notificationType,
        'Unknown notification type',
      );
  }
}

export function SelectVariantAndChannelModal({
  isOpen,
  state,
  canViewWebhooks,
  onCreateChannelChange,
  onExistingChannelChange,
  onVariantChange,
  onNewChannelTypeChange,
  onPrevious,
  onComplete,
  onClose,
}: Props) {
  const { graphId } = useRouteParams(variantRouteConfig);
  const { data, loading, error } = useQuery<
    GraphQLTypes.SelectVariantAndChannelModalQuery,
    GraphQLTypes.SelectVariantAndChannelModalQueryVariables
  >(selectVariantAndChannelModalQuery, {
    variables: {
      graphId,
    },
  });
  const [variantLabelProps, setVariantLabelProps] = useState<unknown>();

  const existingChannelOptions = sortBy(
    data?.service?.channels?.filter((channel) =>
      isAllowedChannelType(state?.notificationType, channel.__typename),
    ) ?? [],
    [
      // Put all channels _with_ a name, before all those without
      (channel) => (channel.name ? -1 : 1),
      (channel) => (channel.name ?? '[without a name]').toLowerCase(),
      // Consistently sort same names by their capitalization
      (channel) => channel.name,
    ],
  );

  const notificationType = state?.notificationType ?? 'ScheduledSummary';
  const existingChannelEnabled =
    (state?.variant || notificationType === 'ProposalLifecycleSubscription') &&
    existingChannelOptions.length > 0;
  const createNewChannelAvaiable =
    notificationType === 'ProposalLifecycleSubscription' || !!state?.variant;

  const canSeeSlackChannelType = isAllowedChannelType(
    state?.notificationType,
    'SlackChannel',
  );
  const canSeeWebhookChannelType =
    isAllowedChannelType(state?.notificationType, 'WebhookChannel') &&
    canViewWebhooks;

  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <Form
        onSubmit={() => {
          onComplete();
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Add notification</ModalTitle>
            <ModalDescription>
              {getSubscriptionTypeDisplayCopy(notificationType)}
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <Loading />
            ) : error ? (
              <ErrorMessage className="mb-10" />
            ) : (
              <>
                {state?.notificationType !==
                  'ProposalLifecycleSubscription' && (
                  <label
                    {...(typeof variantLabelProps === 'object'
                      ? variantLabelProps
                      : {})}
                  >
                    <div className="mb-2 text-base font-semibold">Variant</div>
                    <Select
                      id="variant-select"
                      className="h-10 w-full"
                      matchTriggerWidth={true}
                      value={state?.variant}
                      renderTriggerNode={(selectedItem) => (
                        <>{selectedItem?.children ?? 'Select variant'}</>
                      )}
                      labelPropsCallbackRef={setVariantLabelProps}
                      onChange={(evt) =>
                        evt.target.value && onVariantChange(evt.target.value)
                      }
                    >
                      {sortBy(data?.service?.variants ?? [], [
                        (variant) => variant.name.toLowerCase(),
                        // Consistently sort same names by their capitalization
                        (variant) => variant.name,
                      ]).map((v) => (
                        <option key={v.id}>{v.name}</option>
                      ))}
                    </Select>
                  </label>
                )}

                <label className="flex">
                  <Radio
                    isChecked={state?.createNewChannel === false}
                    isDisabled={!existingChannelEnabled}
                    className="mr-3 mt-0.5"
                    onChange={(evt) =>
                      onCreateChannelChange(!evt.target.checked)
                    }
                  />
                  <div className="grow text-base">
                    <div
                      className={classnames(
                        'font-semibold leading-normal',
                        existingChannelEnabled
                          ? 'text-neutral'
                          : 'text-disabled',
                      )}
                    >
                      Send to an existing notification channel
                    </div>
                    <Select
                      id="channel-select"
                      className="mt-2 h-10 w-full"
                      matchTriggerWidth={true}
                      renderTriggerNode={(selectedItem) => (
                        <>{selectedItem?.children ?? 'Select channel'}</>
                      )}
                      value={state?.targetChannel?.id}
                      disabled={
                        !(
                          existingChannelEnabled &&
                          state?.createNewChannel === false
                        )
                      }
                      onChange={(evt) => {
                        const existingChannel = data?.service?.channels?.find(
                          (c) => c.id === evt.target.value,
                        );
                        if (existingChannel) {
                          onExistingChannelChange(existingChannel);
                        }
                      }}
                    >
                      {existingChannelOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          <div className="flex items-center">
                            <img
                              alt="channel logo"
                              className="mr-2 size-4"
                              src={channelTypeLogo[c.__typename]}
                            />
                            {c.name}
                          </div>
                        </option>
                      ))}
                    </Select>
                  </div>
                </label>
                <label className="flex">
                  <Radio
                    isChecked={state?.createNewChannel === true}
                    isDisabled={!createNewChannelAvaiable}
                    className="mr-2 mt-0.5"
                    onChange={(evt) =>
                      onCreateChannelChange(evt.target.checked)
                    }
                  />
                  <div className="grow text-base">
                    <div
                      className={classnames(
                        'font-semibold leading-normal',
                        !createNewChannelAvaiable
                          ? 'text-disabled'
                          : 'text-neutral',
                      )}
                    >
                      Create new notification channel
                    </div>
                    <Select
                      id="new-channel-type-select"
                      className="mt-2 h-10 w-full"
                      matchTriggerWidth={true}
                      disabled={
                        state?.createNewChannel !== true ||
                        !createNewChannelAvaiable
                      }
                      value={state?.newChannelType}
                      renderTriggerNode={(selectedItem) => (
                        <>{selectedItem?.children ?? 'Select channel type'}</>
                      )}
                      onChange={(evt) => {
                        if (
                          evt.target.value === 'WebhookChannel' ||
                          evt.target.value === 'SlackChannel'
                        ) {
                          onNewChannelTypeChange(evt.target.value);
                        }
                      }}
                    >
                      {canSeeWebhookChannelType && (
                        <option value="WebhookChannel">
                          <img
                            alt="channel logo"
                            className="mr-2 size-4"
                            src={channelTypeLogo.WebhookChannel}
                          />
                          Webhook
                        </option>
                      )}
                      {canSeeSlackChannelType && (
                        <option value="SlackChannel">
                          <img
                            alt="channel logo"
                            className="mr-2 size-4"
                            src={channelTypeLogo.SlackChannel}
                          />
                          Slack
                        </option>
                      )}
                    </Select>
                  </div>
                </label>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <ButtonGroup>
              <Button variant="secondary" type="button" onClick={onPrevious}>
                Previous
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={
                  !(
                    (state?.variant ||
                      notificationType === 'ProposalLifecycleSubscription') &&
                    (state?.newChannelType || state?.targetChannel)
                  )
                }
              >
                Next
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
}
